// src/components/ActivityLists.js — Reports → Sponsors / Tickets / Payments for any dates
// [marker: fixes-oct21 v1]
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { RangePicker, rangePreset } from './AccountReports';

const money = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const METHOD = { check: 'Check', cash: 'Cash', cardknox: 'Card', card: 'Card', wire: 'Wire', ach: 'ACH', zelle: 'Zelle', paypal: 'PayPal', money_order: 'Money order', other: 'Other', comp: 'Comp' };
const STATUS = { historical: 'Before Oct 1 (QuickBooks)', pending: 'Waiting to deposit', deposited: 'Deposited' };

const DEFS = {
  sponsors: {
    url: '/fin-reports/activity/sponsors',
    hint: 'Every sponsorship created in these dates, or for an event held in these dates.',
    totals: ['amount'],
    columns: [
      { key: 'date', label: 'Added', width: 100, type: 'date' },
      { key: 'event_name', label: 'Event', width: 230 },
      { key: 'event_date', label: 'Event Date', width: 100, type: 'date' },
      { key: 'sponsor_name', label: 'Sponsor', width: 210, render: r => <strong>{r.sponsor_name}</strong> },
      { key: 'level', label: 'Level', width: 150 },
      { key: 'amount', label: 'Amount', width: 110, type: 'money' },
      { key: 'payment_status', label: 'Status', width: 100 },
      { key: 'invoice_number', label: 'Invoice', width: 115 },
      { key: 'balance_due', label: 'Still Owed', width: 105, type: 'money' },
      { key: 'contact_name', label: 'Contact', width: 150, defaultHidden: true },
      { key: 'sponsor_email', label: 'Email', width: 190, defaultHidden: true },
      { key: 'paid_date', label: 'Paid', width: 100, type: 'date', defaultHidden: true },
    ],
    open: r => (r.invoice_id ? `/invoices/${r.invoice_id}` : r.event_id ? `/events/${r.event_id}` : null),
  },
  tickets: {
    url: '/fin-reports/activity/tickets',
    hint: 'Every ticket made in these dates — website or staff. Unfinished website checkouts are cleared after two days.',
    totals: ['amount_paid'],
    columns: [
      { key: 'date', label: 'Date', width: 100, type: 'date' },
      { key: 'event_name', label: 'Event', width: 230 },
      { key: 'attendee', label: 'Attendee', width: 180, render: r => <strong>{r.attendee}</strong> },
      { key: 'buyer_business_name', label: 'Company', width: 170 },
      { key: 'ticket_type', label: 'Ticket', width: 120 },
      { key: 'payment_status', label: 'Status', width: 90 },
      { key: 'amount_paid', label: 'Paid', width: 95, type: 'money' },
      { key: 'source', label: 'From', width: 85 },
      { key: 'ticket_number', label: 'Ticket #', width: 130, defaultHidden: true },
      { key: 'buyer_email', label: 'Email', width: 190, defaultHidden: true },
      { key: 'checked_in', label: 'Checked In', width: 95, type: 'bool', defaultHidden: true },
    ],
    open: r => (r.event_id ? `/events/${r.event_id}` : null),
  },
  payments: {
    url: '/fin-reports/activity/payments',
    hint: 'Every payment received in these dates — checks, cards and the rest. Filter the Method column to see only card payments.',
    totals: ['amount'],
    columns: [
      { key: 'date', label: 'Received', width: 100, type: 'date' },
      { key: 'customer', label: 'From', width: 220, render: r => <strong>{r.customer || '—'}</strong> },
      { key: 'method', label: 'Method', width: 90, text: r => METHOD[r.method] || r.method },
      { key: 'amount', label: 'Amount', width: 110, type: 'money' },
      { key: 'invoices', label: 'Invoices', width: 160 },
      { key: 'check_number', label: 'Check #', width: 90 },
      { key: 'status', label: 'Deposit', width: 170, text: r => STATUS[r.status] || r.status },
      { key: 'deposited_date', label: 'Deposited', width: 100, type: 'date' },
      { key: 'reference', label: 'Reference', width: 140, defaultHidden: true },
      { key: 'memo', label: 'Memo', width: 180, defaultHidden: true },
    ],
    open: () => null,
  },
};

export default function ActivityList({ kind }) {
  const def = DEFS[kind];
  const navigate = useNavigate();
  const [range, setRange] = useState(rangePreset('ytd'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`${def.url}?from=${range.from}&to=${range.to}`).then(r => setRows(r.data?.rows || []))
      .catch(() => toast.error('Could not load the list.')).finally(() => setLoading(false));
  }, [def.url, range]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <RangePicker value={range} onChange={setRange} />
        <span style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>{def.hint}</span>
      </div>
      <DataGrid gridKey={`activity-${kind}`} rows={rows} loading={loading} columns={def.columns} totals={def.totals}
                onRowClick={r => { const to = def.open(r); if (to) navigate(to); }} emptyText="Nothing in these dates." />
    </div>
  );
}
